import { parseServerMessage, SYNC_CLOSE_DEVICE_REVOKED, SYNC_PROTOCOL_VERSION, MAX_SUBSCRIBE_DOCS, MAX_UPDATE_BYTES, SNAPSHOT_THRESHOLD_UPDATES, SNAPSHOT_THRESHOLD_BYTES, MAX_SNAPSHOT_BYTES, parseClientMessage } from './chunk-WZE4WKKF.js';
import { deviceRevokedReasonSchema } from './chunk-PLMCPVKY.js';
import { encryptAwareness, decryptAwareness, decryptDocUpdate, encryptDocUpdate } from './chunk-PXX4LS7M.js';
import { uuidv7 } from './chunk-RPUENDSP.js';
import { base64UrlDecodedLength } from './chunk-EWGKAU72.js';
import { toBase64Url, fromBase64Url } from './chunk-LAMR6P2E.js';
import * as Y from 'yjs';

// src/sync/storage.ts
var EMPTY_DOC_STATE = Object.freeze({
  lastSeq: 0,
  updatesSinceSnapshot: 0,
  bytesSinceSnapshot: 0
});
var MemorySyncStorage = class {
  docs = /* @__PURE__ */ new Map();
  outbox = [];
  async getDocState(docId) {
    const s = this.docs.get(docId);
    return s ? { ...s } : null;
  }
  async setDocState(docId, state) {
    this.docs.set(docId, { ...state });
  }
  async getDocStates(docIds) {
    const out = /* @__PURE__ */ new Map();
    for (const id of docIds) {
      const s = this.docs.get(id);
      if (s) out.set(id, { ...s });
    }
    return out;
  }
  async setDocStates(entries) {
    for (const [id, state] of entries) this.docs.set(id, { ...state });
  }
  async enqueue(item) {
    this.outbox.push({ ...item });
  }
  async listOutbox() {
    return this.outbox.map((i) => ({ ...i }));
  }
  async removeOutbox(clientMsgId) {
    const i = this.outbox.findIndex((o) => o.clientMsgId === clientMsgId);
    if (i >= 0) this.outbox.splice(i, 1);
  }
};
var REMOTE_ORIGIN = /* @__PURE__ */ Symbol("datascratch.sync.remote");
var WS_OPEN = 1;
var KeyUnavailableError = class extends Error {
  constructor(docId) {
    super(`key of ${docId} is not available yet`);
    this.docId = docId;
  }
  docId;
  name = "KeyUnavailableError";
};
var MissingKey = class {
};
var PAUSED = /* @__PURE__ */ Symbol("paused");
var SyncClient = class {
  opts;
  docs = /* @__PURE__ */ new Map();
  listeners = {
    status: /* @__PURE__ */ new Set(),
    deviceRevoked: /* @__PURE__ */ new Set(),
    synced: /* @__PURE__ */ new Set(),
    error: /* @__PURE__ */ new Set(),
    awareness: /* @__PURE__ */ new Set(),
    remoteUpdate: /* @__PURE__ */ new Set(),
    skipped: /* @__PURE__ */ new Set(),
    keyMissing: /* @__PURE__ */ new Set(),
    storage: /* @__PURE__ */ new Set()
  };
  stableTimer = null;
  /** Consecutive rate_limited answers (for its own growing delay). */
  rateLimits = 0;
  rateLimitedUntil = 0;
  storageFailing = false;
  skippedLog = /* @__PURE__ */ new Map();
  recoveryTried = /* @__PURE__ */ new Set();
  /** Documents paused once by keyMaybeStale since their last successful decrypt. */
  staleTried = /* @__PURE__ */ new Set();
  sentWaiters = /* @__PURE__ */ new Map();
  socket = null;
  _status = "idle";
  welcomed = false;
  attempt = 0;
  authFailures = 0;
  reconnectTimer = null;
  running = false;
  /** Serializes storage access and message handling. */
  chain = Promise.resolve();
  /** In-memory mirror of the stored outbox (loaded once; this client is its only writer). */
  outbox = null;
  /** Doc states changed since the last batch write. */
  dirty = /* @__PURE__ */ new Set();
  stateTimer = null;
  constructor(options) {
    this.opts = {
      batchMs: 30,
      missingKeyReportMs: 3e4,
      stableAfterMs: 1e4,
      storageRetryMs: 5e3,
      stateFlushMs: 200,
      maxAuthFailures: 3,
      random: Math.random,
      ...options
    };
  }
  get status() {
    return this._status;
  }
  on(event, fn) {
    this.listeners[event].add(fn);
    return () => this.listeners[event].delete(fn);
  }
  /** Start connecting (and keep reconnecting until stop()). */
  start() {
    if (this.running || this._status === "revoked") return;
    this.running = true;
    this.attempt = 0;
    this.authFailures = 0;
    this.connect();
  }
  /** Close the connection and stop reconnecting. Pending outbox items stay stored. */
  async stop() {
    this.running = false;
    this.clearReconnect();
    if (this.stableTimer) clearTimeout(this.stableTimer);
    this.stableTimer = null;
    if (this.resendTimer) clearTimeout(this.resendTimer);
    this.resendTimer = null;
    for (const [docId, entry] of this.docs) await this.flushPending(docId, entry);
    const s = this.socket;
    this.socket = null;
    this.welcomed = false;
    if (s) {
      s.onclose = null;
      s.close(1e3, "client stop");
    }
    this.setStatus("stopped");
    await this.chain;
    await this.flushStates();
  }
  /** Stop for good after a revocation (D-53) and tell the app once. */
  revoke(reason) {
    if (this._status === "revoked") return;
    this.running = false;
    this.clearReconnect();
    if (this.stableTimer) clearTimeout(this.stableTimer);
    this.stableTimer = null;
    if (this.resendTimer) clearTimeout(this.resendTimer);
    this.resendTimer = null;
    const s = this.socket;
    this.socket = null;
    this.welcomed = false;
    if (s) {
      s.onclose = null;
      s.close(1e3, "device revoked");
    }
    this.setStatus("revoked");
    for (const fn of this.listeners.deviceRevoked) fn(reason);
  }
  /** Reconnect now (e.g. the OS reports the network is back, or after re-login). */
  reconnect() {
    if (this._status === "revoked") return;
    this.running = true;
    this.attempt = 0;
    this.authFailures = 0;
    this.clearReconnect();
    if (this.socket) {
      const s = this.socket;
      this.socket = null;
      s.onclose = null;
      s.close(1e3, "reconnect");
    }
    this.connect();
  }
  /** Start syncing a document. Local updates are pushed; remote ones applied with REMOTE_ORIGIN. */
  async addDoc(docId, doc) {
    await this.addDocs([{ docId, doc }]);
  }
  /**
   * Start syncing many documents at once (first sync, app start): one batch
   * read of their cursors and one `subscribe` message.
   */
  async addDocs(list) {
    await this.register(list);
  }
  /**
   * Start syncing documents in stored mode: no Y.Doc in memory, remote
   * updates go to `storedDocs.onUpdates`. Open one with openDoc() when the
   * user opens it. Requires the `storedDocs` option.
   */
  async addStoredDocs(docIds) {
    if (!this.opts.storedDocs) throw new Error("addStoredDocs needs the storedDocs option");
    await this.register(docIds.map((docId) => ({ docId, doc: null })));
  }
  /**
   * Switch a stored document to a live Y.Doc. `load` builds the Y.Doc from the
   * saved state; it runs while no sync message is being processed, so no
   * remote update can slip between the saved state and the live document.
   */
  async openDoc(docId, load) {
    const entry = this.docs.get(docId);
    if (!entry) throw new Error(`document ${docId} is not synced`);
    return this.serial(async () => {
      if (entry.doc) return entry.doc;
      await this.flushStored(docId, entry);
      const doc = await load();
      this.attach(docId, entry, doc);
      return doc;
    });
  }
  /**
   * Switch a live document back to stored mode. Local edits are queued first;
   * `save` persists the Y.Doc's final state and runs before any later remote
   * update is handed to `storedDocs.onUpdates`, so nothing can be overwritten.
   */
  async closeDoc(docId, save) {
    if (!this.opts.storedDocs) throw new Error("closeDoc needs the storedDocs option");
    const entry = this.docs.get(docId);
    if (!entry?.doc) return;
    await this.flushPending(docId, entry);
    await this.serial(async () => {
      if (!entry.doc) return;
      entry.detach();
      await this.enqueuePending(docId, entry);
      await save(entry.doc);
      entry.doc = null;
      entry.detach = () => {
      };
    });
  }
  isLive(docId) {
    return this.docs.get(docId)?.doc != null;
  }
  attach(docId, entry, doc) {
    const handler = (update, origin) => {
      if (origin === REMOTE_ORIGIN) return;
      this.queueLocalUpdate(docId, update);
    };
    doc.on("update", handler);
    entry.doc = doc;
    entry.detach = () => doc.off("update", handler);
  }
  async register(list) {
    for (const { docId } of list) {
      if (this.docs.has(docId)) throw new Error(`document ${docId} is already synced`);
    }
    const ids = list.map((d) => d.docId);
    const storage = this.opts.storage;
    const states = storage.getDocStates ? await storage.getDocStates(ids) : new Map(
      (await Promise.all(ids.map(async (id) => [id, await storage.getDocState(id)]))).filter((e) => e[1] !== null)
    );
    for (const { docId, doc } of list) {
      const entry = {
        doc: null,
        stored: [],
        paused: null,
        state: states.get(docId) ?? { ...EMPTY_DOC_STATE },
        synced: false,
        pending: [],
        pendingBytes: 0,
        timer: null,
        snapshotInFlight: null,
        detach: () => {
        }
      };
      this.docs.set(docId, entry);
      if (doc) this.attach(docId, entry, doc);
    }
    if (this.welcomed && list.length > 0) {
      this.sendSubscribe(
        ids.map((docId) => ({ docId, since: this.docs.get(docId).state.lastSeq }))
      );
      const added = new Set(ids);
      await this.serial(async () => {
        for (const item of (await this.outboxItems()).values()) {
          if (added.has(item.docId)) this.sendItem(item);
        }
      });
    }
  }
  /** Hand buffered remote updates of a stored document to the app. */
  async flushStored(docId, entry) {
    if (entry.stored.length === 0) return;
    const updates = entry.stored;
    entry.stored = [];
    await this.opts.storedDocs.onUpdates(docId, updates);
  }
  /** Stop syncing a document (its pending local updates are still stored and sent). */
  async removeDoc(docId) {
    const entry = this.docs.get(docId);
    if (!entry) return;
    await this.flushPending(docId, entry);
    await this.serial(() => this.flushStored(docId, entry));
    await this.flushStates();
    entry.detach();
    this.docs.delete(docId);
    if (this.welcomed) this.send({ type: "unsubscribe", docIds: [docId] });
  }
  /**
   * The key of a paused document is now available: resume from the last
   * applied seq (nothing was skipped while it waited). Queued local edits are
   * sent too. Safe to call for documents that are not paused.
   */
  async keyAvailable(docId) {
    const entry = this.docs.get(docId);
    if (!entry) return;
    await this.serial(async () => {
      if (entry.paused) {
        if (entry.paused.timer) clearTimeout(entry.paused.timer);
        entry.paused = null;
        if (this.welcomed) {
          this.sendSubscribe([{ docId, since: entry.state.lastSeq }]);
        }
      }
      await this.enqueuePending(docId, entry);
    });
  }
  /**
   * Fetch skipped items of a document again (e.g. after the host learned a
   * retired key and returns it from `alternateKeysFor`). Returns false when
   * nothing was skipped. Items that fail again are logged again.
   */
  async retrySkipped(docId) {
    const entry = this.docs.get(docId);
    const log = this.skippedLog.get(docId);
    if (!entry || !log || log.length === 0) return false;
    const since = Math.max(0, Math.min(...log.map((s) => s.seq)) - 1);
    this.skippedLog.delete(docId);
    this.recoveryTried.delete(docId);
    await this.serial(async () => {
      if (this.welcomed) this.sendSubscribe([{ docId, since }]);
    });
    return true;
  }
  /** True while the outbox storage is failing (edits wait in memory). */
  get storageFull() {
    return this.storageFailing;
  }
  isPaused(docId) {
    return this.docs.get(docId)?.paused != null;
  }
  /** Items skipped for a document (or all documents) since this client started. */
  getSkipped(docId) {
    if (docId) return [...this.skippedLog.get(docId) ?? []];
    return [...this.skippedLog.values()].flat();
  }
  isSynced(docId) {
    return this.docs.get(docId)?.synced ?? false;
  }
  /** Send an awareness update (y-protocols encoding); dropped while offline. */
  async sendAwareness(docId, update) {
    if (!this.welcomed || !this.docs.get(docId)?.doc) return;
    const key = await this.getKey(docId);
    if (!key) return;
    this.send({
      type: "awareness",
      docId,
      payload: toBase64Url(encryptAwareness(key, docId, update))
    });
  }
  /**
   * Resolves once every local edit of `docId` made so far has been accepted
   * by the server (or dropped as unacceptable) — e.g. before telling the
   * server a key rotation is done (D-54). Waits across reconnects.
   */
  async whenSent(docId) {
    const entry = this.docs.get(docId);
    if (entry) await this.flushPending(docId, entry);
    await this.chain;
    const outbox = await this.outboxItems();
    if (![...outbox.values()].some((i) => i.docId === docId)) return;
    await new Promise((resolve) => {
      const list = this.sentWaiters.get(docId) ?? [];
      list.push(resolve);
      this.sentWaiters.set(docId, list);
    });
  }
  /** Flush batched local updates to the outbox now. */
  async flush() {
    for (const [docId, entry] of this.docs) await this.flushPending(docId, entry);
    await this.chain;
    await this.flushStates();
  }
  /**
   * Upload a snapshot now (e.g. when leaving a document after many own edits,
   * for version history). Returns false when the document is not caught up yet
   * or a snapshot is already in flight.
   */
  async requestSnapshot(docId) {
    const entry = this.docs.get(docId);
    if (!entry) throw new Error(`document ${docId} is not synced`);
    await this.flushPending(docId, entry);
    return this.serial(async () => {
      if (!entry.synced || entry.snapshotInFlight) return false;
      await this.enqueueSnapshot(docId, entry, false);
      return entry.snapshotInFlight !== null;
    });
  }
  /**
   * Subscribe again and resend the document's queued messages — e.g. after
   * `POST /v1/docs` registered a document created offline (the first
   * subscribe got `doc_not_found`).
   */
  async resubscribe(docId) {
    const entry = this.docs.get(docId);
    if (!entry) throw new Error(`document ${docId} is not synced`);
    await this.serial(async () => {
      if (!this.welcomed) return;
      this.sendSubscribe([{ docId, since: entry.state.lastSeq }]);
      for (const item of (await this.outboxItems()).values())
        if (item.docId === docId) this.sendItem(item);
    });
  }
  /** Resend every outbox item (e.g. after registering a document that got `doc_not_found`). */
  async resendOutbox() {
    await this.serial(async () => {
      if (!this.welcomed) return;
      for (const item of (await this.outboxItems()).values()) this.sendItem(item);
    });
  }
  // -------------------------------------------------------------------------
  // Connection
  // -------------------------------------------------------------------------
  setStatus(status) {
    if (this._status === status) return;
    this._status = status;
    for (const fn of this.listeners.status) fn(status);
  }
  emitError(error) {
    for (const fn of this.listeners.error) fn(error);
  }
  clearReconnect() {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }
  connect() {
    if (!this.running) return;
    this.setStatus("connecting");
    this.welcomed = false;
    const factory = this.opts.createSocket ?? ((url) => {
      const WS = globalThis.WebSocket;
      if (!WS) throw new Error("no global WebSocket: pass createSocket");
      return new WS(url);
    });
    let socket;
    try {
      socket = factory(this.opts.url);
    } catch (e) {
      this.emitError({ code: "internal", message: `cannot open socket: ${String(e)}` });
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;
    socket.onopen = () => {
      void this.sendHello(socket);
    };
    socket.onmessage = (ev) => {
      if (socket !== this.socket) return;
      const raw = typeof ev.data === "string" ? ev.data : String(ev.data);
      const msg = parseServerMessage(raw);
      if (!msg) {
        this.emitError({ code: "internal", message: "invalid server message" });
        return;
      }
      void this.serial(() => this.handle(msg));
    };
    socket.onclose = (ev) => {
      if (socket !== this.socket) return;
      if (ev?.code === SYNC_CLOSE_DEVICE_REVOKED) {
        const reason = deviceRevokedReasonSchema.safeParse(ev.reason);
        this.revoke(reason.success ? reason.data : "user");
        return;
      }
      this.socket = null;
      this.welcomed = false;
      if (this.stableTimer) clearTimeout(this.stableTimer);
      this.stableTimer = null;
      for (const entry of this.docs.values()) {
        entry.synced = false;
        entry.snapshotInFlight = null;
      }
      if (this.running && this._status !== "unauthorized" && this._status !== "stopped" && this._status !== "revoked") {
        this.setStatus("offline");
        this.scheduleReconnect();
      }
    };
    socket.onerror = () => {
    };
  }
  async sendHello(socket) {
    let auth;
    try {
      auth = await this.opts.auth();
    } catch (e) {
      this.emitError({ code: "unauthorized", message: `auth provider failed: ${String(e)}` });
      socket.close(1e3, "no auth");
      return;
    }
    if (socket !== this.socket) return;
    this.send({
      type: "hello",
      protocol: SYNC_PROTOCOL_VERSION,
      auth,
      deviceId: this.opts.deviceId,
      client: this.opts.client
    });
  }
  scheduleReconnect() {
    if (!this.running || this.reconnectTimer) return;
    const initial = this.opts.backoff?.initialMs ?? 500;
    const max = this.opts.backoff?.maxMs ?? 3e4;
    const exp = Math.min(max, initial * 2 ** this.attempt);
    let delay = exp / 2 + exp / 2 * this.opts.random();
    delay = Math.max(delay, this.rateLimitedUntil - Date.now());
    this.attempt++;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }
  /** subscribe in messages of at most MAX_SUBSCRIBE_DOCS distinct documents. */
  sendSubscribe(docs) {
    const unique = /* @__PURE__ */ new Map();
    for (const d of docs) unique.set(d.docId, Math.min(d.since, unique.get(d.docId) ?? d.since));
    const all = [...unique].map(([docId, since]) => ({ docId, since }));
    for (let i = 0; i < all.length; i += MAX_SUBSCRIBE_DOCS) {
      this.send({ type: "subscribe", docs: all.slice(i, i + MAX_SUBSCRIBE_DOCS) });
    }
  }
  send(msg) {
    const s = this.socket;
    if (!s || s.readyState !== WS_OPEN) return false;
    s.send(JSON.stringify(msg));
    return true;
  }
  resendTimer = null;
  scheduleResend() {
    if (this.resendTimer) return;
    this.resendTimer = setTimeout(
      () => {
        this.resendTimer = null;
        void this.resendOutbox();
      },
      Math.max(0, this.rateLimitedUntil - Date.now())
    );
  }
  sendItem(item) {
    if (Date.now() < this.rateLimitedUntil) {
      this.scheduleResend();
      return;
    }
    if (!this.docs.has(item.docId)) return;
    if (item.kind === "push") {
      this.send({
        type: "push",
        docId: item.docId,
        clientMsgId: item.clientMsgId,
        payload: item.payload
      });
    } else {
      this.send({
        type: "snapshot",
        docId: item.docId,
        clientMsgId: item.clientMsgId,
        baseSeq: item.baseSeq ?? 0,
        payload: item.payload
      });
    }
  }
  async outboxItems() {
    if (!this.outbox) {
      const items = await this.opts.storage.listOutbox();
      this.outbox ??= new Map(items.map((i) => [i.clientMsgId, i]));
    }
    return this.outbox;
  }
  /** Store an outbox item; false (and storage state "failing") when storage rejects it. */
  async enqueue(item) {
    try {
      await this.opts.storage.enqueue(item);
    } catch (e) {
      if (!this.storageFailing) {
        this.storageFailing = true;
        for (const fn of this.listeners.storage) fn(false, e);
      }
      this.emitError({ code: "storage_failed", message: String(e), docId: item.docId });
      return false;
    }
    (await this.outboxItems()).set(item.clientMsgId, item);
    if (this.storageFailing) {
      this.storageFailing = false;
      for (const fn of this.listeners.storage) fn(true);
    }
    return true;
  }
  async removeOutbox(clientMsgId) {
    const outbox = await this.outboxItems();
    const docId = outbox.get(clientMsgId)?.docId;
    outbox.delete(clientMsgId);
    await this.opts.storage.removeOutbox(clientMsgId);
    if (docId) this.notifySent(docId, outbox);
  }
  notifySent(docId, outbox) {
    const waiters = this.sentWaiters.get(docId);
    if (!waiters) return;
    for (const item of outbox.values()) if (item.docId === docId) return;
    this.sentWaiters.delete(docId);
    for (const fn of waiters) fn();
  }
  /** Persist cursors lazily: a lost write only means re-fetching a few updates. */
  markDirty(docId) {
    this.dirty.add(docId);
    this.stateTimer ??= setTimeout(() => {
      this.stateTimer = null;
      void this.flushStates();
    }, this.opts.stateFlushMs);
  }
  async flushStates() {
    if (this.stateTimer) clearTimeout(this.stateTimer);
    this.stateTimer = null;
    if (this.dirty.size === 0) return;
    const entries = [];
    for (const id of this.dirty) {
      const entry = this.docs.get(id);
      if (entry) entries.push([id, { ...entry.state }]);
    }
    this.dirty.clear();
    const storage = this.opts.storage;
    try {
      if (storage.setDocStates) await storage.setDocStates(entries);
      else for (const [id, state] of entries) await storage.setDocState(id, state);
    } catch (e) {
      for (const [id] of entries) this.dirty.add(id);
      this.emitError({ code: "storage_failed", message: String(e) });
      this.stateTimer ??= setTimeout(() => {
        this.stateTimer = null;
        void this.flushStates();
      }, this.opts.storageRetryMs);
    }
  }
  serial(fn) {
    const next = this.chain.then(fn, fn);
    this.chain = next.catch(() => void 0);
    return next;
  }
  // -------------------------------------------------------------------------
  // Server messages
  // -------------------------------------------------------------------------
  async handle(msg) {
    switch (msg.type) {
      case "welcome": {
        this.welcomed = true;
        this.authFailures = 0;
        this.setStatus("online");
        if (this.stableTimer) clearTimeout(this.stableTimer);
        this.stableTimer = setTimeout(() => {
          this.stableTimer = null;
          this.attempt = 0;
          this.rateLimits = 0;
        }, this.opts.stableAfterMs);
        this.sendSubscribe([...this.docs].map(([docId, e]) => ({ docId, since: e.state.lastSeq })));
        for (const item of (await this.outboxItems()).values()) this.sendItem(item);
        return;
      }
      case "updates": {
        const entry = this.docs.get(msg.docId);
        if (!entry || entry.paused) return;
        const skippedBefore = this.skippedLog.get(msg.docId)?.length ?? 0;
        let paused = false;
        if (msg.snapshot) {
          const ref = { seq: msg.snapshot.seq, deviceId: null, kind: "snapshot" };
          const r = await this.applyRemote(msg.docId, entry, msg.snapshot.payload, ref);
          if (r === PAUSED) paused = true;
          else {
            if (r) {
              entry.state.updatesSinceSnapshot = 0;
              entry.state.bytesSinceSnapshot = 0;
            }
            entry.state.lastSeq = Math.max(entry.state.lastSeq, msg.snapshot.seq);
          }
        }
        for (const item of msg.items) {
          if (paused) break;
          const ref = {
            seq: item.seq,
            deviceId: item.deviceId,
            kind: "update",
            ...item.author ? { author: item.author } : {}
          };
          const size = await this.applyRemote(msg.docId, entry, item.payload, ref);
          if (size === PAUSED) {
            paused = true;
            break;
          }
          this.count(entry, size);
          entry.state.lastSeq = Math.max(entry.state.lastSeq, item.seq);
        }
        await this.flushStored(msg.docId, entry);
        if (paused) {
          this.markDirty(msg.docId);
          return;
        }
        this.markDirty(msg.docId);
        if (msg.upToDate && !entry.synced) {
          entry.synced = true;
          for (const fn of this.listeners.synced) fn(msg.docId);
        }
        const skippedNow = (this.skippedLog.get(msg.docId)?.length ?? 0) > skippedBefore;
        if (skippedNow && msg.upToDate && this.opts.recoverSkippedFromSnapshot && !this.recoveryTried.has(msg.docId)) {
          this.recoveryTried.add(msg.docId);
          this.sendSubscribe([{ docId: msg.docId, since: 0 }]);
        }
        await this.maybeSnapshot(msg.docId, entry);
        return;
      }
      case "update": {
        const entry = this.docs.get(msg.docId);
        if (!entry || entry.paused) return;
        const ref = {
          seq: msg.seq,
          deviceId: msg.deviceId,
          kind: "update",
          ...msg.author ? { author: msg.author } : {}
        };
        const size = await this.applyRemote(msg.docId, entry, msg.payload, ref);
        if (size === PAUSED) return;
        this.count(entry, size);
        entry.state.lastSeq = Math.max(entry.state.lastSeq, msg.seq);
        await this.flushStored(msg.docId, entry);
        this.markDirty(msg.docId);
        await this.maybeSnapshot(msg.docId, entry);
        return;
      }
      case "ack": {
        const item = (await this.outboxItems()).get(msg.clientMsgId);
        await this.removeOutbox(msg.clientMsgId);
        const entry = this.docs.get(msg.docId);
        if (!entry) return;
        entry.state.lastSeq = Math.max(entry.state.lastSeq, msg.seq);
        if (item?.kind === "snapshot" || entry.snapshotInFlight === msg.clientMsgId) {
          entry.snapshotInFlight = null;
          entry.state.updatesSinceSnapshot = 0;
          entry.state.bytesSinceSnapshot = 0;
        } else if (item) {
          this.count(entry, item.size);
        }
        this.markDirty(msg.docId);
        await this.maybeSnapshot(msg.docId, entry);
        return;
      }
      case "awareness": {
        if (!this.docs.get(msg.docId)?.doc) return;
        try {
          const key = await this.getKey(msg.docId);
          if (!key) return;
          const payload = decryptAwareness(key, msg.docId, fromBase64Url(msg.payload));
          for (const fn of this.listeners.awareness) fn(msg.docId, payload, msg.deviceId);
        } catch {
        }
        return;
      }
      case "error":
        if (msg.code === "device_revoked") {
          this.revoke(msg.reason ?? "user");
          return;
        }
        await this.handleError(msg.code, msg.message, msg.ref, msg.retryAfterMs);
        return;
    }
  }
  async handleError(code, message, ref, retryAfterMs) {
    const item = ref ? (await this.outboxItems()).get(ref) : void 0;
    const docId = item?.docId ?? (ref && this.docs.has(ref) ? ref : void 0);
    this.emitError({ code, message, ...ref ? { ref } : {}, ...docId ? { docId } : {} });
    switch (code) {
      case "unauthorized":
        this.authFailures++;
        if (this.authFailures >= this.opts.maxAuthFailures) {
          this.setStatus("unauthorized");
          this.running = false;
        }
        this.socket?.close(1e3, "unauthorized");
        return;
      case "protocol_mismatch":
        this.running = false;
        this.setStatus("stopped");
        this.socket?.close(1e3, "protocol mismatch");
        return;
      case "rate_limited": {
        this.rateLimits++;
        const own = Math.min(6e4, 5e3 * 2 ** (this.rateLimits - 1));
        this.rateLimitedUntil = Date.now() + (retryAfterMs ?? own);
        this.scheduleResend();
        return;
      }
      case "payload_too_large":
        if (item) {
          await this.removeOutbox(item.clientMsgId);
          const entry = this.docs.get(item.docId);
          if (item.kind === "push" && entry) await this.enqueueSnapshot(item.docId, entry, true);
          else if (entry) entry.snapshotInFlight = null;
        }
        return;
      case "forbidden":
        if (item) await this.removeOutbox(item.clientMsgId);
        return;
      default:
        return;
    }
  }
  /**
   * Decrypt and apply; returns the plaintext size, 0 when the item is skipped.
   * A bad item never stops the document: it is recorded and reported, and the
   * cursor still advances past it.
   */
  async applyRemote(docId, entry, payload, ref) {
    let update;
    try {
      update = await this.decrypt(docId, payload);
    } catch (e) {
      if (e instanceof MissingKey) {
        this.pause(docId, entry);
        return PAUSED;
      }
      if (this.opts.keyMaybeStale && !this.staleTried.has(docId) && await this.opts.keyMaybeStale(docId)) {
        this.staleTried.add(docId);
        this.pause(docId, entry);
        return PAUSED;
      }
      this.skip(docId, ref, "decrypt_failed", e);
      return 0;
    }
    this.staleTried.delete(docId);
    if (!entry.doc) {
      try {
        Y.decodeUpdate(update);
      } catch (e) {
        this.skip(docId, ref, "apply_failed", e);
        return 0;
      }
      entry.stored.push(update);
      for (const fn of this.listeners.remoteUpdate) fn(docId, update, ref);
      return update.length;
    }
    try {
      Y.applyUpdate(entry.doc, update, REMOTE_ORIGIN);
    } catch (e) {
      this.skip(docId, ref, "apply_failed", e);
      return 0;
    }
    for (const fn of this.listeners.remoteUpdate) fn(docId, update, ref);
    return update.length;
  }
  /** keyFor, with "not available yet" normalized to null. */
  async getKey(docId) {
    try {
      return await this.opts.keyFor(docId) ?? null;
    } catch (e) {
      if (e instanceof KeyUnavailableError) return null;
      throw e;
    }
  }
  pause(docId, entry) {
    if (entry.paused) return;
    const timer = setTimeout(() => {
      if (entry.paused) {
        this.emitError({
          code: "key_missing",
          message: `waiting for the key of ${docId} for over ${this.opts.missingKeyReportMs} ms`,
          docId
        });
      }
    }, this.opts.missingKeyReportMs);
    entry.paused = { since: Date.now(), timer };
    for (const fn of this.listeners.keyMissing) fn(docId);
  }
  async decrypt(docId, payload) {
    const envelope = fromBase64Url(payload);
    const key = await this.getKey(docId);
    try {
      if (!key) throw new MissingKey();
      return decryptDocUpdate(key, docId, envelope);
    } catch (first) {
      for (const key2 of await this.opts.alternateKeysFor?.(docId) ?? []) {
        try {
          return decryptDocUpdate(key2, docId, envelope);
        } catch {
        }
      }
      throw first;
    }
  }
  skip(docId, ref, reason, error) {
    const item = {
      docId,
      seq: ref.seq,
      deviceId: ref.deviceId,
      kind: ref.kind,
      reason,
      message: String(error),
      at: Date.now()
    };
    const log = this.skippedLog.get(docId) ?? [];
    if (!log.some((s) => s.seq === item.seq && s.kind === item.kind)) log.push(item);
    this.skippedLog.set(docId, log);
    for (const fn of this.listeners.skipped) fn(item);
    this.emitError({
      code: reason,
      message: `${ref.kind} seq ${ref.seq} skipped: ${String(error)}`,
      docId
    });
  }
  count(entry, size) {
    entry.state.updatesSinceSnapshot++;
    entry.state.bytesSinceSnapshot += size;
  }
  // -------------------------------------------------------------------------
  // Local updates and snapshots
  // -------------------------------------------------------------------------
  queueLocalUpdate(docId, update) {
    const entry = this.docs.get(docId);
    if (!entry) return;
    entry.pending.push(update);
    entry.pendingBytes += update.length;
    if (entry.pendingBytes >= MAX_UPDATE_BYTES / 2) {
      void this.flushPending(docId, entry);
      return;
    }
    if (!entry.timer) {
      entry.timer = setTimeout(() => void this.flushPending(docId, entry), this.opts.batchMs);
    }
  }
  flushPending(docId, entry) {
    if (entry.timer) clearTimeout(entry.timer);
    entry.timer = null;
    if (entry.pending.length === 0) return Promise.resolve();
    return this.serial(() => this.enqueuePending(docId, entry));
  }
  /** Merge pending local updates into one outbox push (call inside serial()). */
  async enqueuePending(docId, entry) {
    if (entry.timer) clearTimeout(entry.timer);
    entry.timer = null;
    if (entry.pending.length === 0) return;
    const updates = entry.pending;
    entry.pending = [];
    entry.pendingBytes = 0;
    if (this.opts.readOnly?.(docId)) return;
    {
      const update = updates.length === 1 ? updates[0] : Y.mergeUpdates(updates);
      if (update.length > MAX_UPDATE_BYTES) {
        await this.enqueueSnapshot(docId, entry, true);
        return;
      }
      const key = await this.getKey(docId);
      if (!key) {
        entry.pending = [update, ...entry.pending];
        entry.pendingBytes += update.length;
        this.pause(docId, entry);
        return;
      }
      const item = {
        clientMsgId: uuidv7(),
        docId,
        kind: "push",
        payload: toBase64Url(encryptDocUpdate(key, docId, update)),
        size: update.length,
        createdAt: Date.now()
      };
      if (!await this.enqueue(item)) {
        entry.pending = [update, ...entry.pending];
        entry.pendingBytes += update.length;
        entry.timer ??= setTimeout(
          () => void this.flushPending(docId, entry),
          this.opts.storageRetryMs
        );
        return;
      }
      if (this.welcomed) this.sendItem(item);
    }
  }
  async maybeSnapshot(docId, entry) {
    if (!entry.synced || entry.snapshotInFlight) return;
    const maxUpdates = this.opts.snapshotThresholds?.updates ?? SNAPSHOT_THRESHOLD_UPDATES;
    const maxBytes = this.opts.snapshotThresholds?.bytes ?? SNAPSHOT_THRESHOLD_BYTES;
    if (entry.state.updatesSinceSnapshot <= maxUpdates && entry.state.bytesSinceSnapshot <= maxBytes)
      return;
    await this.enqueueSnapshot(docId, entry, false);
  }
  async enqueueSnapshot(docId, entry, force) {
    if (entry.snapshotInFlight && !force) return;
    if (this.opts.readOnly?.(docId)) return;
    let state;
    if (entry.doc) {
      this.opts.beforeSnapshot?.(docId, entry.doc);
      state = Y.encodeStateAsUpdate(entry.doc);
    } else {
      await this.flushStored(docId, entry);
      const saved = await this.opts.storedDocs.loadState(docId);
      if (!saved) return;
      state = Y.mergeUpdates([saved]);
    }
    if (state.length > MAX_SNAPSHOT_BYTES) {
      this.emitError({ code: "too_large", message: "document exceeds the snapshot limit", docId });
      return;
    }
    const key = await this.getKey(docId);
    if (!key) return;
    const item = {
      clientMsgId: uuidv7(),
      docId,
      kind: "snapshot",
      baseSeq: entry.state.lastSeq,
      payload: toBase64Url(encryptDocUpdate(key, docId, state)),
      size: state.length,
      createdAt: Date.now()
    };
    entry.snapshotInFlight = item.clientMsgId;
    if (!await this.enqueue(item)) {
      entry.snapshotInFlight = null;
      return;
    }
    if (this.welcomed) this.sendItem(item);
  }
};

// src/sync/memory-hub.ts
var MemorySyncHub = class {
  docs = /* @__PURE__ */ new Map();
  connections = /* @__PURE__ */ new Set();
  revoked = /* @__PURE__ */ new Map();
  opts;
  constructor(options = {}) {
    this.opts = {
      autoCreateDocs: true,
      catchUpChunk: 100,
      now: Date.now,
      preferSnapshot: true,
      ...options
    };
  }
  /** Register a document (the equivalent of `POST /v1/docs`). */
  registerDoc(docId) {
    if (!this.docs.has(docId)) this.docs.set(docId, { seq: 0, items: [], snapshot: null });
  }
  /** Attach a transport. `send` delivers a text frame to the client. */
  connect(send, close) {
    const conn = {
      send: (msg) => send(JSON.stringify(msg)),
      close,
      principal: null,
      deviceId: null,
      subscriptions: /* @__PURE__ */ new Set()
    };
    this.connections.add(conn);
    return {
      receive: (raw) => this.receive(conn, raw),
      disconnect: () => this.connections.delete(conn)
    };
  }
  /** Close every connection (simulates a server restart or network loss). */
  dropAll() {
    for (const c of [...this.connections]) {
      this.connections.delete(c);
      c.close();
    }
  }
  /**
   * Revoke a device (D-53): its open connections get `error device_revoked`
   * and close 4410; later hellos from it are refused the same way.
   */
  revokeDevice(deviceId, reason) {
    this.revoked.set(deviceId, reason);
    for (const c of [...this.connections]) {
      if (c.deviceId === deviceId) this.kickRevoked(c, reason);
    }
  }
  kickRevoked(conn, reason) {
    conn.send({ type: "error", code: "device_revoked", message: "device revoked", reason });
    this.connections.delete(conn);
    conn.close(SYNC_CLOSE_DEVICE_REVOKED, reason);
  }
  get connectionCount() {
    return this.connections.size;
  }
  /** A WebSocketFactory whose sockets talk to this hub asynchronously, in order. */
  socketFactory() {
    return () => {
      const socket = {
        readyState: 0,
        onopen: null,
        onmessage: null,
        onclose: null,
        onerror: null,
        send: (data) => {
          if (socket.readyState !== 1) throw new Error("socket not open");
          deliver(() => link.receive(data));
        },
        close: () => shut()
      };
      let closed = false;
      const shut = (code = 1e3, reason = "") => {
        if (closed) return;
        closed = true;
        socket.readyState = 3;
        link.disconnect();
        deliver(() => socket.onclose?.({ code, reason }));
      };
      const link = this.connect(
        (raw) => {
          if (!closed) deliver(() => socket.onmessage?.({ data: raw }));
        },
        (code, reason) => shut(code, reason)
      );
      deliver(() => {
        if (closed) return;
        socket.readyState = 1;
        socket.onopen?.({});
      });
      return socket;
    };
  }
  error(conn, code, message, ref) {
    conn.send({ type: "error", code, message, ...ref ? { ref } : {} });
  }
  doc(conn, docId, ref) {
    let d = this.docs.get(docId);
    if (!d && this.opts.autoCreateDocs) {
      this.registerDoc(docId);
      d = this.docs.get(docId);
    }
    if (!d) {
      this.error(conn, "doc_not_found", `document ${docId} is not registered`, ref ?? docId);
      return null;
    }
    return d;
  }
  receive(conn, raw) {
    const msg = parseClientMessage(raw);
    if (!msg) {
      const hello = safeJson(raw);
      if (hello?.type === "hello" && hello.protocol !== SYNC_PROTOCOL_VERSION) {
        this.error(
          conn,
          "protocol_mismatch",
          `protocol ${String(hello.protocol)} is not supported`
        );
      } else {
        this.error(conn, "bad_request", "invalid message");
      }
      return;
    }
    if (msg.type === "hello") {
      const revoked = this.revoked.get(msg.deviceId);
      if (revoked) {
        this.kickRevoked(conn, revoked);
        return;
      }
      const principal = this.opts.authenticate ? this.opts.authenticate(msg.auth) : {
        userId: "00000000-0000-7000-8000-000000000000",
        canRead: () => true,
        canWrite: () => true
      };
      if (!principal) {
        this.error(conn, "unauthorized", "invalid credentials");
        conn.close();
        this.connections.delete(conn);
        return;
      }
      conn.principal = principal;
      conn.deviceId = msg.deviceId;
      conn.send({
        type: "welcome",
        userId: principal.userId,
        serverTime: new Date(this.opts.now()).toISOString(),
        protocol: SYNC_PROTOCOL_VERSION
      });
      return;
    }
    const p = conn.principal;
    if (!p) {
      this.error(conn, "unauthorized", "hello first");
      return;
    }
    switch (msg.type) {
      case "subscribe":
        for (const { docId, since } of msg.docs) {
          if (!p.canRead(docId)) {
            this.error(conn, "forbidden", "no access", docId);
            continue;
          }
          const d = this.doc(conn, docId);
          if (!d) continue;
          conn.subscriptions.add(docId);
          this.catchUp(conn, docId, d, since);
        }
        return;
      case "unsubscribe":
        for (const id of msg.docIds) conn.subscriptions.delete(id);
        return;
      case "push":
      case "snapshot": {
        if (!p.canWrite(msg.docId)) {
          this.error(conn, "forbidden", "read-only access", msg.clientMsgId);
          return;
        }
        const limit = msg.type === "push" ? MAX_UPDATE_BYTES : MAX_SNAPSHOT_BYTES;
        if (base64UrlDecodedLength(msg.payload.length) > limit) {
          this.error(conn, "payload_too_large", "payload too large", msg.clientMsgId);
          return;
        }
        const d = this.doc(conn, msg.docId, msg.clientMsgId);
        if (!d) return;
        if (msg.type === "push") {
          const userId = conn.principal?.userId;
          const item = {
            seq: ++d.seq,
            payload: msg.payload,
            deviceId: conn.deviceId,
            ...userId ? { author: { kind: "user", userId } } : {}
          };
          d.items.push(item);
          conn.send({ type: "ack", docId: msg.docId, clientMsgId: msg.clientMsgId, seq: item.seq });
          for (const other of this.connections) {
            if (other !== conn && other.subscriptions.has(msg.docId)) {
              other.send({ type: "update", docId: msg.docId, ...item });
            }
          }
        } else {
          const baseSeq = Math.min(msg.baseSeq, d.seq);
          if (!d.snapshot || baseSeq >= d.snapshot.seq) {
            d.snapshot = { seq: baseSeq, payload: msg.payload };
            d.items = d.items.filter((i) => i.seq > baseSeq);
          }
          conn.send({
            type: "ack",
            docId: msg.docId,
            clientMsgId: msg.clientMsgId,
            seq: baseSeq
          });
        }
        return;
      }
      case "awareness":
        if (!conn.subscriptions.has(msg.docId)) return;
        for (const other of this.connections) {
          if (other !== conn && other.subscriptions.has(msg.docId)) {
            other.send({
              type: "awareness",
              docId: msg.docId,
              payload: msg.payload,
              deviceId: conn.deviceId
            });
          }
        }
        return;
    }
  }
  catchUp(conn, docId, d, since) {
    const oldest = d.items[0]?.seq ?? d.seq + 1;
    const useSnapshot = d.snapshot !== null && since < d.snapshot.seq && (this.opts.preferSnapshot || since < oldest - 1);
    const from = useSnapshot ? d.snapshot.seq : since;
    const items = d.items.filter((i) => i.seq > from);
    const chunk = this.opts.catchUpChunk;
    let first = true;
    for (let i = 0; i === 0 || i < items.length; i += chunk) {
      const part = items.slice(i, i + chunk);
      conn.send({
        type: "updates",
        docId,
        snapshot: first && useSnapshot ? { ...d.snapshot } : null,
        items: part,
        upToDate: i + chunk >= items.length
      });
      first = false;
    }
  }
};
function safeJson(raw) {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
function deliver(fn) {
  setTimeout(fn, 0);
}

export { EMPTY_DOC_STATE, KeyUnavailableError, MemorySyncHub, MemorySyncStorage, REMOTE_ORIGIN, SyncClient };
//# sourceMappingURL=chunk-MZ5O3YAG.js.map
//# sourceMappingURL=chunk-MZ5O3YAG.js.map